import { Input } from "@/frontend/shared/ui/Input";
import { Typography } from "@/frontend/shared/ui/Typography";
import styles from "./OrgSettingsSection.module.scss";
import { useState } from "react";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { EditIcon } from "@/frontend/shared/assets/icons";
import { useQuery } from "@tanstack/react-query";
import { getProfileDetails } from "../../model/services/getProfileDetails";
import { Loader } from "@/frontend/shared/ui/Loader/Loader";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { AxiosError } from "axios";
import { stringValidation } from "@/frontend/shared/lib/stringValidation/stringValidation";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { getOrganizationById } from "@/frontend/pages/SettingsPage/model/services/getOrganizationById";
import { patchUpdateOrgDetails } from "@/frontend/pages/SettingsPage/model/services/patchUpdateOrgDetails";
import { OrgPictureModal } from "@/frontend/pages/SettingsPage/ui/OrgPictureModal/OrgPictureModal";
import { patchOrganizationImage } from "@/frontend/pages/SettingsPage/model/services/patchOrganizationImage";
import { deleteOrganizationImage } from "@/frontend/pages/SettingsPage/model/services/deleteOrganizationImage";

export function OrgSettingsSection() {
  const { data: profileData, isLoading: isLoadingProfileData } = useQuery({
    queryKey: ["profileData"],
    queryFn: async () => {
      const data = await getProfileDetails();
      return data;
    },
    refetchOnWindowFocus: false,
    throwOnError: (error) => {
      console.error(error);

      reportErrorToSentry(error, "SETTINGS: /me");
      return true;
    }
  });

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ["orgData", profileData?.organizationId],
    queryFn: async () => {
      if (!profileData?.organizationId) return null;
      const data = await getOrganizationById(profileData.organizationId);
      setOrgData({
        name: data.name,
        profilePhotoImgUrl: data.profilePhotoImgUrl || ""
      });
      setName(data.name);
      setOnUploadImage(data.profilePhotoImgUrl);
      return data;
    },
    refetchOnWindowFocus: false,
    throwOnError: (error) => {
      console.error(error);

      reportErrorToSentry(error, "SETTINGS: /organizations/:id");
      return true;
    }
  });

  const [orgData, setOrgData] = useState({
    name: data?.name || "",
    profilePhotoImgUrl: data?.profilePhotoImgUrl || ""
  });
  const [name, setName] = useState(orgData?.name);
  const [isEditing, setIsEditing] = useState(false);
  const [onUploadImage, setOnUploadImage] = useState(orgData?.profilePhotoImgUrl || null);
  const [requestIsLoading, setRequestIsLoading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);

  const [isShown, setIsShown] = useState({ name: false });

  async function onUploadBase64Image(img: string | null) {
    if (img === orgData.profilePhotoImgUrl || img === null) return;

    if (!img) {
      snackbarConstructor("Failed to load image. Try again later.", "error");
      return;
    }

    setIsImageLoading(true);

    if (!profileData?.organizationId) return;
    try {
      await patchOrganizationImage({ id: profileData?.organizationId, base64Img: img });
      setIsImageLoading(false);
      await refetch();
      setIsImageLoading(false);
      setOnUploadImage(img);
    } catch {
      setIsImageLoading(false);
      snackbarConstructor("Failed to upload image. Try again later.", "error");
    }
  }

  async function removeImage() {
    if (!profileData?.organizationId) return;
    try {
      setIsImageLoading(true);
      await deleteOrganizationImage(profileData?.organizationId);
      setIsImageLoading(false);
      setIsEditing(false);
      refetch();
    } catch (err) {
      const axiosError = err as AxiosError<{ message: string }>;
      const errorMessage = axiosError.response?.data?.message || "Unexpected error occurred";
      snackbarConstructor(errorMessage, "error");
    }
  }

  if (isLoading || isFetching) {
    return (
      <div className={styles.loadingContainer}>
        <Loader />
      </div>
    );
  }

  async function sendUpdatedDetailsData() {
    setRequestIsLoading(true);
    if (!profileData?.organizationId) return;
    try {
      await patchUpdateOrgDetails({
        id: profileData?.organizationId,
        name
      });
      refetch();
      setIsShown({ name: false });
    } catch (err) {
      const axiosError = err as AxiosError<{ message: string }>;
      const errorMessage = axiosError.response?.data?.message || "Unexpected error occurred";
      snackbarConstructor(errorMessage, "error");
    } finally {
      setRequestIsLoading(false);
    }
  }

  return (
    <div className={styles.container}>
      <Typography className={styles.title} color="greyscale800" variant="heading-6">
        Settings
      </Typography>
      <div className={styles.contentContainer}>
        <div className={styles.imageContainer}>
          {orgData.profilePhotoImgUrl ? (
            <img className={styles.image} src={`${orgData.profilePhotoImgUrl}?${new Date().getTime()}`} alt="" />
          ) : (
            <div className={styles.image} />
          )}
          <div className={styles.editIconContainer} onClick={() => setIsEditing(!isEditing)}>
            <EditIcon />
          </div>
          <OrgPictureModal
            isOpen={isEditing}
            onClose={() => setIsEditing(!isEditing)}
            onUpload={onUploadBase64Image}
            onUploadFile={() => {}}
            onRemove={removeImage}
            image={orgData.profilePhotoImgUrl ? orgData.profilePhotoImgUrl : ""}
            isLoading={isImageLoading}
          />
        </div>
        <div className={styles.content}>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              <Typography variant="label-4" color="greyscale700" className={styles.nameTitle}>
                Name
              </Typography>
              {!isShown.name && (
                <Typography
                  onClick={() => setIsShown({ ...isShown, name: !isShown.name })}
                  className={styles.editBtn}
                  variant="label-5"
                >
                  Edit
                </Typography>
              )}
            </div>
            {isShown.name ? (
              <div className={styles.nameInputsContainer}>
                <div className={styles.nameInputsBlock}>
                  <Input
                    onChange={(e) => {
                      if (stringValidation(e.target.value)) setName(e.target.value);
                    }}
                    value={name}
                  />
                </div>
                <div className={styles.nameButtonsContainer}>
                  <NormalButton
                    size="medium"
                    onClick={() => {
                      setOrgData({ ...orgData, name });
                      sendUpdatedDetailsData();
                    }}
                    isDisabled={requestIsLoading || name.length === 0}
                  >
                    Save
                  </NormalButton>
                  <NormalButton
                    size="medium"
                    variant="tertiary"
                    color="greyscale600"
                    onClick={() => {
                      setName(orgData.name);
                      setIsShown({ ...isShown, name: !isShown.name });
                    }}
                  >
                    Cancel
                  </NormalButton>
                </div>
              </div>
            ) : (
              <Typography variant="body-2" color="greyscale900">
                {orgData.name}
              </Typography>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
